import { useEffect } from 'react';
import { CalendarDays, ChartColumn, FlaskConical, Package, Receipt, ShoppingBasket, UserPlus, UserSearch, Users, type LucideIcon } from 'lucide-react';
import type { KpiTone } from '@/components/ui/KpiTile';

/**
 * The one Quick Actions list (docs/design/dashboard, region 10 and
 * QuickActions_Options.html, options B + C). Three surfaces read it and none
 * keeps its own copy: the Dashboard's Quick Actions card, the topbar ⚡ menu,
 * and the Ctrl+K command palette's "Actions" group.
 *
 * Each action just switches App.tsx's module, like the sidebar. `shortcut` is
 * the digit for Alt+<digit>; it belongs to the action, not to its position on
 * screen, so a role that cannot see one tile does not shift every later
 * shortcut by one.
 */
export interface QuickAction {
  id: string;
  label: string;
  /** Module id from App.tsx's NAV — also what decides whether the role sees it. */
  mod: string;
  icon: LucideIcon;
  tone: KpiTone;
  shortcut: number;
  /** Extra words the palette matches on, besides the label. */
  keywords: string;
  /** One line under the label in the palette. */
  hint: string;
}

export const QUICK_ACTIONS: QuickAction[] = [
  { id: 'intake', label: 'New OPD Intake', mod: 'voice', icon: UserPlus, tone: 'blue', shortcut: 1, keywords: 'register new patient walk-in voice', hint: 'Opens Intake' },
  { id: 'search', label: 'Search Patient', mod: 'records', icon: UserSearch, tone: 'green', shortcut: 2, keywords: 'find records uhid history', hint: 'Opens Patient records' },
  { id: 'appointments', label: 'Appointments', mod: 'appointments', icon: CalendarDays, tone: 'violet', shortcut: 3, keywords: 'book slot schedule doctor', hint: 'Opens Appointments' },
  { id: 'queue', label: "Today's Queue", mod: 'queue', icon: Users, tone: 'orange', shortcut: 4, keywords: 'token waiting check-in', hint: 'Opens Queue' },
  { id: 'billing', label: 'Billing', mod: 'billing', icon: Receipt, tone: 'teal', shortcut: 5, keywords: 'bill invoice opd payment', hint: 'Opens Billing' },
  { id: 'pharmacy-sale', label: 'Pharmacy Sale', mod: 'pharmacy', icon: ShoppingBasket, tone: 'red', shortcut: 6, keywords: 'counter walk-in medicine sell bill', hint: 'Opens Pharmacy' },
  { id: 'lab', label: 'Lab Orders', mod: 'laboratory', icon: FlaskConical, tone: 'indigo', shortcut: 7, keywords: 'laboratory test sample', hint: 'Opens Laboratory' },
  { id: 'add-stock', label: 'Add Stock', mod: 'pharmacy', icon: Package, tone: 'green', shortcut: 8, keywords: 'inventory goods receipt grn medicine', hint: 'Opens Pharmacy' },
  { id: 'reports', label: 'Reports', mod: 'reports', icon: ChartColumn, tone: 'violet', shortcut: 9, keywords: 'mis day book analytics', hint: 'Opens Reports' },
];

export const actionsFor = (modules: readonly string[] | undefined) =>
  QUICK_ACTIONS.filter(a => modules?.includes(a.mod));

/** "Alt+1" or "⌥1" on a Mac. */
export function shortcutLabel(digit: number | string): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return mac ? `⌥${digit}` : `Alt+${digit}`;
}

/**
 * Global keyboard shortcuts: Alt+1..9 runs that action, Alt+Q calls
 * `onToggleMenu`. Matched on `event.code` (Digit1, KeyQ) because on a Mac the
 * Option key changes `event.key` to a symbol. Actions the role cannot use are
 * ignored, never navigated to.
 */
export function useQuickActionShortcuts(actions: QuickAction[], onRun: (a: QuickAction) => void, onToggleMenu: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.repeat) return;
      if (e.code === 'KeyQ') { e.preventDefault(); onToggleMenu(); return; }
      const m = /^Digit([1-9])$/.exec(e.code);
      if (!m) return;
      const action = actions.find(a => a.shortcut === Number(m[1]));
      if (!action) return;
      e.preventDefault();
      onRun(action);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions, onRun, onToggleMenu]);
}
